/**
 * DetalhesAgendamentoDialog - Componente extraído do AgendamentosPage
 * Exibe detalhes de um agendamento (usado por Profissionais)
 * 
 * FASE 1 da refatoração - Extração de diálogos isolados
 */
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { 
  Clock, 
  User, 
  Phone, 
  Mail, 
  MessageCircle,
  CreditCard,
  MapPin,
  PlayCircle,
  CheckCircle,
  XCircle,
  Edit,
  FileText
} from 'lucide-react';
import { parseLocalDateTime, formatDateTime } from '../../utils/dateUtils';

// Calcular idade
const calcularIdade = (dataNascimento) => {
  if (!dataNascimento) return null;
  const hoje = new Date();
  const nascimento = new Date(dataNascimento);
  let idade = hoje.getFullYear() - nascimento.getFullYear();
  const m = hoje.getMonth() - nascimento.getMonth();
  if (m < 0 || (m === 0 && hoje.getDate() < nascimento.getDate())) {
    idade--;
  }
  return idade;
};

// Estado inicial do dialog
export const initialDetailsDialogState = {
  open: false,
  agendamento: null
};

/**
 * DetalhesAgendamentoDialog Component
 * 
 * @param {Object} props
 * @param {Object} props.dialogState - Estado do dialog
 * @param {Function} props.setDialogState - Setter do estado do dialog
 * @param {Function} props.getStatusBadge - Função para renderizar badge de status
 * @param {Function} props.getTransicoesPermitidas - Função para obter transições permitidas
 * @param {Function} props.onUpdateStatus - Callback para atualizar status
 * @param {Function} props.onOpenRemanejar - Callback para abrir modal de remanejamento (unificado)
 */
const DetalhesAgendamentoDialog = ({
  dialogState,
  setDialogState,
  getStatusBadge,
  getTransicoesPermitidas,
  onUpdateStatus,
  onOpenRemanejar
}) => {
  const navigate = useNavigate();
  const agendamento = dialogState.agendamento;
  
  const handleClose = () => {
    setDialogState(initialDetailsDialogState);
  };

  if (!agendamento) return null;

  const statusAtual = agendamento.status;
  const transicoesPermitidas = getTransicoesPermitidas(statusAtual, 'profissional');

  return (
    <Dialog 
      open={dialogState.open} 
      onOpenChange={(open) => setDialogState({ open, agendamento: open ? agendamento : null })}
    >
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Manrope, sans-serif', color: '#2C3632' }}>
            Detalhes do Agendamento
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-2">
          {/* Status */}
          <div className="flex items-center justify-between">
            {getStatusBadge(agendamento.status)}
            <span className="text-sm" style={{ color: '#5C6B64' }}>
              {formatDateTime(agendamento.data_hora_inicio, "EEEE, d 'de' MMMM")}
            </span>
          </div>
          
          {/* Horário */}
          <div className="flex items-center gap-3 p-3 rounded-lg" style={{ backgroundColor: '#F8F6F3' }}>
            <Clock className="h-5 w-5" style={{ color: '#4A6B5D' }} />
            <div>
              <p className="font-semibold" style={{ color: '#2C3632' }}>
                {formatDateTime(agendamento.data_hora_inicio, 'HH:mm')} - {formatDateTime(agendamento.data_hora_fim, 'HH:mm')}
              </p>
              <p className="text-sm" style={{ color: '#5C6B64' }}>
                Duração: {Math.round((parseLocalDateTime(agendamento.data_hora_fim) - parseLocalDateTime(agendamento.data_hora_inicio)) / 60000)} min
              </p>
            </div>
          </div>
          
          {/* Paciente */}
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider" style={{ color: '#5C6B64' }}>Paciente</Label>
            <div className="p-3 rounded-lg border" style={{ borderColor: '#EAE6DF' }}>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#E8F0EB' }}>
                  <User className="h-5 w-5" style={{ color: '#4A6B5D' }} />
                </div>
                <div className="flex-1">
                  <p className="font-semibold" style={{ color: '#2C3632' }}>
                    {agendamento.pacientes?.nome_completo || 'N/A'}
                  </p>
                  {agendamento.pacientes?.data_nascimento && (
                    <p className="text-sm" style={{ color: '#5C6B64' }}>
                      {calcularIdade(agendamento.pacientes.data_nascimento)} anos
                    </p>
                  )}
                </div>
              </div>
              
              {/* Contatos */}
              <div className="mt-3 space-y-2">
                {agendamento.pacientes?.telefone_responsavel && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="h-4 w-4" style={{ color: '#5C6B64' }} />
                      <span style={{ color: '#4A6B5D' }}>
                        {agendamento.pacientes.telefone_responsavel}
                      </span>
                    </div>
                    <a
                      href={`https://api.whatsapp.com/send?phone=55${agendamento.pacientes.telefone_responsavel.replace(/\D/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors hover:opacity-90"
                      style={{ backgroundColor: '#25D366', color: 'white' }}
                      data-testid="whatsapp-button"
                    >
                      <MessageCircle className="h-3.5 w-3.5" />
                      WhatsApp
                    </a>
                  </div>
                )}
                {agendamento.pacientes?.email_responsavel && (
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="h-4 w-4" style={{ color: '#5C6B64' }} />
                    <span style={{ color: '#4A6B5D' }}>
                      {agendamento.pacientes.email_responsavel}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Convênio */}
          {agendamento.convenios && (
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider" style={{ color: '#5C6B64' }}>Convênio</Label>
              <div className="flex items-center gap-2 p-2 rounded" style={{ backgroundColor: '#FDF6EE' }}>
                <CreditCard className="h-4 w-4" style={{ color: '#D4A373' }} />
                <span style={{ color: '#2C3632' }}>{agendamento.convenios.nome}</span>
              </div>
            </div>
          )}
          
          {/* Sala */}
          {agendamento.salas && (
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider" style={{ color: '#5C6B64' }}>Sala</Label>
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4" style={{ color: '#5C6B64' }} />
                <span style={{ color: '#2C3632' }}>{agendamento.salas.nome}</span>
              </div>
            </div>
          )}
          
          {/* Observações */}
          {agendamento.observacoes && (
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider" style={{ color: '#5C6B64' }}>Observações</Label>
              <p className="text-sm p-2 rounded" style={{ backgroundColor: '#F8F6F3', color: '#2C3632' }}>
                {agendamento.observacoes}
              </p>
            </div>
          )}
        </div>
        
        <DialogFooter className="flex-col gap-2">
          {/* Botões de Transição de Status para Profissional */}
          {transicoesPermitidas.length > 0 && (
            <div className="w-full space-y-2">
              <p className="text-xs text-center" style={{ color: '#5C6B64' }}>Alterar status:</p>
              <div className="flex flex-wrap gap-2 justify-center">
                {transicoesPermitidas.includes('em_atendimento') && statusAtual === 'aguardando' && (
                  <Button 
                    size="sm"
                    onClick={() => {
                      onUpdateStatus(agendamento.id, 'em_atendimento');
                      handleClose();
                    }}
                    style={{ backgroundColor: '#4A6B5D' }}
                    className="text-white"
                    data-testid="btn-iniciar-atendimento"
                  >
                    <PlayCircle className="h-4 w-4 mr-1" />
                    Iniciar Atendimento
                  </Button>
                )}
                {transicoesPermitidas.includes('finalizado') && statusAtual === 'em_atendimento' && (
                  <Button 
                    size="sm"
                    onClick={() => {
                      onUpdateStatus(agendamento.id, 'finalizado');
                      handleClose();
                    }}
                    style={{ backgroundColor: '#7B9E87' }}
                    className="text-white"
                    data-testid="btn-finalizar-atendimento"
                  >
                    <CheckCircle className="h-4 w-4 mr-1" />
                    Finalizar
                  </Button>
                )}
                {transicoesPermitidas.includes('cancelado') && (
                  <Button 
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      onUpdateStatus(agendamento.id, 'cancelado', null, agendamento);
                    }}
                    style={{ borderColor: '#B75D69', color: '#B75D69' }}
                    data-testid="btn-cancelar-agendamento"
                  >
                    <XCircle className="h-4 w-4 mr-1" />
                    Cancelar
                  </Button>
                )}
              </div>
            </div>
          )}
          
          <div className="flex gap-2 w-full sm:w-auto flex-wrap">
            <Button 
              variant="outline" 
              onClick={handleClose}
              className="flex-1 sm:flex-none"
            >
              Fechar
            </Button>
            {agendamento.paciente_id && (
              <Button
                variant="outline"
                onClick={() => {
                  handleClose();
                  navigate(`/dashboard/prontuarios?paciente_id=${agendamento.paciente_id}`);
                }}
                style={{ borderColor: '#4A6B5D', color: '#4A6B5D' }}
                className="flex-1 sm:flex-none"
                data-testid="btn-abrir-prontuario"
              >
                <FileText className="h-4 w-4 mr-2" />
                Abrir Prontuário
              </Button>
            )}
            {(agendamento.status === 'agendado' || agendamento.status === 'aguardando' || agendamento.status === 'falta') && (
              <Button 
                onClick={() => {
                  handleClose();
                  onOpenRemanejar(agendamento);
                }}
                style={{ backgroundColor: '#4A6B5D' }}
                className="text-white flex-1 sm:flex-none"
                data-testid="btn-remanejar-agendamento"
              >
                <Edit className="h-4 w-4 mr-2" />
                Remanejar
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DetalhesAgendamentoDialog;
